import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { ArrowRight, ArrowLeft, Check, Calculator, FileText, Star, CreditCard, Info, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALC = ["nettoyage-canape","nettoyage-chaises","nettoyage-fauteuils","nettoyage-matelas","nettoyage-moquette","nettoyage-tapis","nettoyage-terrasse","nettoyage-vitres"];

const TOOLTIPS = {
  terrasse: {
    nettoyage: "Nettoyage haute pression de votre terrasse. Élimine la saleté, la mousse et les dépôts accumulés. Résultat visible immédiatement.",
    traitement: "Nettoyage haute pression + application d'un traitement qui prolonge la durée de vie de votre terrasse et ralentit la réapparition des salissures.",
    protection: "Notre formule complète : nettoyage + traitement + application d'une protection longue durée qui imperméabilise la surface et empêche la mousse et les taches de s'installer. Durée de protection : 12-24 mois.",
  },
  vitres: {
    nettoyage: "Nettoyage professionnel de vos vitres, intérieur et extérieur. Sans traces.",
    traitement: "Nettoyage + application d'un traitement qui repousse l'eau et les salissures. Vos vitres restent propres plus longtemps.",
    protection: "Formule complète : nettoyage + traitement anti-traces + protection hydrophobe longue durée. L'eau perle sur vos vitres et emporte la saleté.",
  },
  moquette: {
    leger: "Nettoyage de surface pour un entretien régulier. Idéal pour les moquettes peu sollicitées ou un rafraîchissement.",
    profond: "Nettoyage par injection-extraction en profondeur. Élimine les taches, acariens et odeurs incrustés dans les fibres.",
  },
  tapis: {
    leger: "Nettoyage de surface adapté aux tapis synthétiques. Élimine poussière et salissures légères.",
    profond: "Nettoyage en profondeur par injection-extraction. Élimine taches tenaces, odeurs et allergènes.",
  },
};

function tp(tranches, s) {
  if (!tranches || s <= 0) return 0;
  for (const t of tranches) { if (s >= t.min && s < t.max) return t.prix; }
  return tranches[tranches.length - 1]?.prix || 0;
}

function Tooltip({ text, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);
  return (
    <div ref={ref} className="absolute z-50 left-0 right-0 top-full mt-2 bg-white border border-[#062133]/15 rounded-lg shadow-lg p-3 text-xs text-[#062133]/80 leading-relaxed" data-testid="tooltip-popup">
      <button onClick={onClose} className="absolute top-1 right-1 text-[#062133]/40 hover:text-[#062133]"><X size={14} /></button>
      {text}
    </div>
  );
}

export default function PriceCalculator({ onRequestDevis, preselectedSlug }) {
  const [services, setServices] = useState([]);
  const [zones, setZones] = useState([]);
  const [pr, setPr] = useState({});
  const [step, setStep] = useState(1);
  const [sel, setSel] = useState(null);
  const [ch, setCh] = useState({});
  const [tooltip, setTooltip] = useState(null);
  const [contact, setContact] = useState({ prenom: "", nom: "", email: "", localite: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/services`), axios.get(`${API}/zones-deplacement`), axios.get(`${API}/pricing`),
    ]).then(([s, z, p]) => {
      setServices(s.data); setZones(z.data); setPr(p.data);
      // Preselect if prop provided
      if (preselectedSlug) {
        const found = s.data.find(sv => sv.slug === preselectedSlug);
        if (found && CALC.includes(found.slug)) { setSel(found); setStep(2); }
      }
    });
  }, [preselectedSlug]);

  const min = pr.minimum_facturation || 120;
  const slug = sel?.slug;

  // Step count: +1 for contact capture (before result)
  const steps = useMemo(() => {
    if (!slug) return 1;
    if (!CALC.includes(slug)) return 2;
    const m = { "nettoyage-canape": 10, "nettoyage-chaises": 6, "nettoyage-fauteuils": 6, "nettoyage-matelas": 6,
      "nettoyage-moquette": 7, "nettoyage-tapis": 7, "nettoyage-vitres": 7,
      "nettoyage-terrasse": 9 };
    return m[slug] || 2;
  }, [slug]);

  const pick = (s) => { setSel(s); setCh({}); setContact({ prenom: "", nom: "", email: "", localite: "" }); setStep(2); };
  const reset = () => { setStep(preselectedSlug ? 2 : 1); if (!preselectedSlug) { setSel(null); } setCh({}); setContact({ prenom: "", nom: "", email: "", localite: "" }); };

  // ===== Compute total =====
  const result = useMemo(() => {
    if (!sel || !CALC.includes(slug)) return null;
    let svc = 0;
    const zn = ch.zone ? (zones.find(z => z.zone === ch.zone)?.prix || 0) : 0;

    if (slug === "nettoyage-canape") {
      const c = pr.canape; if (!c) return null;
      const { cat, prof, etat, supps, anti_tache } = ch;
      if (cat && prof && c.categories?.[cat]) svc += c.categories[cat][prof] || 0;
      if (etat && c.etat?.[etat]) svc += c.etat[etat].prix;
      if (supps) Object.keys(supps).forEach(k => { if (supps[k] && c.supplements?.[k]) svc += c.supplements[k].prix; });
      if (anti_tache === "oui" && cat && c.anti_tache?.[cat]) svc += c.anti_tache[cat][prof || "standard"] || 0;
    } else if (slug === "nettoyage-chaises") {
      const n = ch.quantite || 0;
      if (n > 0 && pr.chaises?.tarifs?.[String(n)]) svc = pr.chaises.tarifs[String(n)];
    } else if (slug === "nettoyage-fauteuils") {
      const n = ch.quantite || 0;
      if (n > 0 && pr.fauteuils?.tarifs?.[String(n)]) svc = pr.fauteuils.tarifs[String(n)];
    } else if (slug === "nettoyage-matelas") {
      const t = ch.taille;
      if (t && pr.matelas?.tarifs?.[t]) svc = pr.matelas.tarifs[t].prix;
    } else if (slug === "nettoyage-moquette") {
      const s = ch.surface || 0; const f = ch.formule;
      if (s > 0 && f && pr.moquette?.formules?.[f]?.tranches) svc = s * tp(pr.moquette.formules[f].tranches, s);
    } else if (slug === "nettoyage-tapis") {
      const s = ch.surface || 0; const f = ch.formule;
      if (s > 0 && f && pr.tapis?.formules?.[f]?.tranches) svc = s * tp(pr.tapis.formules[f].tranches, s);
    } else if (slug === "nettoyage-vitres") {
      const s = ch.surface || 0; const f = ch.formule;
      if (s > 0 && f && pr.vitres?.formules?.[f]) svc = s * pr.vitres.formules[f].prix_m2;
    } else if (slug === "nettoyage-terrasse") {
      const s = ch.surface || 0; const f = ch.formule;
      if (s > 0 && f && pr.terrasse?.formules?.[f]?.tranches) svc = s * tp(pr.terrasse.formules[f].tranches, s);
      if (ch.opts && ch.type_sol === "bois" && pr.terrasse?.options_exterieur) {
        Object.keys(ch.opts).forEach(k => { if (ch.opts[k] && pr.terrasse.options_exterieur[k]?.prix_m2) svc += s * pr.terrasse.options_exterieur[k].prix_m2; });
      }
    }

    if (svc <= 0) return null;
    const fs = Math.max(svc, min);
    const total = Math.round(fs + zn);
    const tw = ch.paiement === "twint" ? Math.round((fs + zn) * 1.03 * 100) / 100 : null;
    return { total, twint: tw };
  }, [sel, slug, ch, pr, zones, min]);

  // ===== Submit contact + lead =====
  const submitContact = async () => {
    setSubmitting(true);
    try {
      const choicesSummary = Object.entries(ch).filter(([k]) => !["zone","paiement"].includes(k)).map(([k, v]) => {
        if (typeof v === "object") return `${k}: ${Object.keys(v).filter(kk => v[kk]).join(", ")}`;
        return `${k}: ${v}`;
      }).join(", ");

      await axios.post(`${API}/leads`, {
        nom: `${contact.prenom} ${contact.nom}`,
        telephone: "",
        email: contact.email,
        service: sel?.name || "",
        ville: contact.localite,
        type_client: "particulier",
        message: choicesSummary,
        source: "calculateur",
        page_origine: window.location.pathname,
        urgence: "moyen",
        valeur_estimee: result?.total || 0,
        website: "",
      });
    } catch (e) { /* non-blocking */ }
    setSubmitting(false);
    setStep(step + 1);
  };

  const contactValid = contact.prenom.trim() && contact.nom.trim() && contact.email.includes("@") && contact.localite.trim();

  // ===== UI Components =====
  const Opt = ({ s: isSel, onClick, children, tid, hi }) => (
    <button onClick={onClick} data-testid={tid}
      className={`p-3 sm:p-4 border rounded-lg text-left transition-all w-full relative ${
        isSel ? "border-[#336DBF] bg-[#336DBF]/5 ring-1 ring-[#336DBF]" :
        "border-[#062133]/10 hover:border-[#336DBF]/50"}`}>
      {hi && <span className="absolute -top-2.5 left-3 bg-[#336DBF] text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><Star size={10} /> Recommandé</span>}
      {children}
    </button>
  );
  const Nav = ({ back, next, dis, label = "Suivant" }) => (
    <div className="flex justify-center gap-3 mt-6">
      {back && <button onClick={back} className="px-4 py-2.5 border border-[#062133]/20 rounded-md text-sm font-medium text-[#062133]/60 hover:bg-[#F4F6F8]"><ArrowLeft size={16} className="inline mr-1" /> Retour</button>}
      <button onClick={next} disabled={dis} className="px-6 py-2.5 bg-[#336DBF] text-white rounded-md text-sm font-semibold hover:bg-[#2A5A9E] disabled:opacity-40 disabled:cursor-not-allowed" data-testid="calc-next-step">{label} <ArrowRight size={16} className="inline ml-1" /></button>
    </div>
  );

  // ===== Shared Steps =====
  const ZoneStep = ({ prev, next }) => (
    <div data-testid={`calculator-step-${step}`}>
      <p className="text-sm text-[#062133]/60 text-center mb-4">Zone de déplacement</p>
      <div className="max-w-md mx-auto">
        <select value={ch.zone || ""} onChange={e => setCh(c => ({...c, zone: e.target.value}))}
          className="w-full border border-[#062133]/15 rounded-md px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#336DBF] outline-none" data-testid="calc-zone-select">
          <option value="">Sélectionnez votre zone</option>
          {zones.map(z => <option key={z.zone} value={z.zone}>{z.zone}</option>)}
        </select>
      </div>
      <Nav back={() => setStep(prev)} next={() => setStep(next)} dis={!ch.zone} />
    </div>
  );
  const PayStep = ({ prev, next }) => (
    <div data-testid={`calculator-step-${step}`}>
      <p className="text-sm text-[#062133]/60 text-center mb-4">Mode de paiement</p>
      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
        <Opt s={ch.paiement === "cash"} onClick={() => setCh(c => ({...c, paiement: "cash"}))} tid="calc-pay-cash">
          <span className="block text-sm font-semibold text-[#062133]">Cash</span>
          <span className="block text-xs text-[#062133]/60 mt-1">Pas de supplément</span>
        </Opt>
        <Opt s={ch.paiement === "twint"} onClick={() => setCh(c => ({...c, paiement: "twint"}))} tid="calc-pay-twint">
          <span className="block text-sm font-semibold text-[#062133]">Twint</span>
          <span className="block text-xs text-[#062133]/60 mt-1">Supplément de 3%</span>
        </Opt>
      </div>
      <Nav back={() => setStep(prev)} next={() => setStep(next)} dis={!ch.paiement} />
    </div>
  );
  const ContactStep = ({ prev }) => (
    <div data-testid="calculator-contact-step">
      <p className="text-sm text-[#062133]/60 text-center mb-1">Pour recevoir votre estimation</p>
      <p className="text-xs text-[#062133]/40 text-center mb-5">Renseignez vos coordonnées pour recevoir votre estimation personnalisée</p>
      <div className="max-w-md mx-auto grid grid-cols-2 gap-3">
        <input type="text" placeholder="Prénom *" value={contact.prenom} onChange={e => setContact(c => ({...c, prenom: e.target.value}))}
          className="col-span-1 border border-[#062133]/15 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#336DBF] outline-none" data-testid="calc-contact-prenom" />
        <input type="text" placeholder="Nom *" value={contact.nom} onChange={e => setContact(c => ({...c, nom: e.target.value}))}
          className="col-span-1 border border-[#062133]/15 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#336DBF] outline-none" data-testid="calc-contact-nom" />
        <input type="email" placeholder="Email *" value={contact.email} onChange={e => setContact(c => ({...c, email: e.target.value}))}
          className="col-span-2 border border-[#062133]/15 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#336DBF] outline-none" data-testid="calc-contact-email" />
        <input type="text" placeholder="Localité *" value={contact.localite} onChange={e => setContact(c => ({...c, localite: e.target.value}))}
          className="col-span-2 border border-[#062133]/15 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#336DBF] outline-none" data-testid="calc-contact-localite" />
      </div>
      <div className="flex justify-center gap-3 mt-6">
        <button onClick={() => setStep(prev)} className="px-4 py-2.5 border border-[#062133]/20 rounded-md text-sm font-medium text-[#062133]/60 hover:bg-[#F4F6F8]"><ArrowLeft size={16} className="inline mr-1" /> Retour</button>
        <button onClick={submitContact} disabled={!contactValid || submitting} className="px-6 py-2.5 bg-[#336DBF] text-white rounded-md text-sm font-semibold hover:bg-[#2A5A9E] disabled:opacity-40 disabled:cursor-not-allowed" data-testid="calc-submit-contact">
          {submitting ? "..." : "Voir mon estimation"} <ArrowRight size={16} className="inline ml-1" />
        </button>
      </div>
    </div>
  );
  const ResultStep = () => (
    <div className="text-center" data-testid="calculator-result">
      {result ? (
        <>
          <p className="text-sm text-[#062133]/60 mb-4">Votre estimation pour {sel?.name}</p>
          <div className="bg-[#F4F6F8] border border-[#062133]/10 rounded-lg p-6 sm:p-8 max-w-sm mx-auto" data-testid="calc-total-display">
            <p className="text-sm font-medium text-[#062133]/60 mb-1">TOTAL ESTIMÉ</p>
            <p className="text-4xl font-bold text-[#336DBF]" data-testid="calc-total-amount">{result.total} CHF</p>
            {result.twint && <p className="text-sm text-[#062133]/60 mt-2 flex items-center justify-center gap-1"><CreditCard size={14} /> Paiement Twint : <strong className="text-[#062133]">{result.twint.toFixed(2)} CHF</strong></p>}
          </div>
          <p className="text-xs text-[#062133]/40 mt-4 mb-6 max-w-md mx-auto">Ce prix est une estimation. Le devis définitif vous sera communiqué après évaluation de votre situation.</p>
          <button onClick={() => { if (onRequestDevis) onRequestDevis(sel?.name, `Estimation: ${result.total} CHF`); else document.getElementById("devis-section")?.scrollIntoView({behavior:"smooth"}); }}
            className="px-8 py-3.5 bg-[#336DBF] text-white font-semibold rounded-md hover:bg-[#2A5A9E]" data-testid="calc-devis-button">
            <FileText size={18} className="inline mr-2" /> Confirmer et recevoir mon devis
          </button>
        </>
      ) : <p className="text-[#062133]/60">Veuillez remplir toutes les étapes.</p>}
      <button onClick={reset} className="block mx-auto mt-3 text-sm text-[#062133]/60 hover:text-[#062133]" data-testid="calc-restart">Recommencer</button>
    </div>
  );

  // ===== Surface & Formule Steps =====
  const SurfaceStep = ({ prev, next, mx = 200, def = 20 }) => {
    const s = ch.surface || def;
    return (
      <div data-testid={`calculator-step-${step}`}>
        <p className="text-sm text-[#062133]/60 text-center mb-4">Surface en m²</p>
        <div className="max-w-xs mx-auto">
          <input type="range" min={1} max={mx} step={1} value={s} onChange={e => setCh(c => ({...c, surface: parseInt(e.target.value)}))}
            className="w-full accent-[#336DBF] h-2 rounded-lg cursor-pointer" data-testid="calc-surface-slider" />
          <p className="text-3xl font-bold text-[#336DBF] mt-3 text-center">{s} <span className="text-lg font-normal text-[#062133]/60">m²</span></p>
        </div>
        <Nav back={() => setStep(prev)} next={() => { setCh(c => ({...c, surface: c.surface || def})); setStep(next); }} />
      </div>
    );
  };
  const FormuleStep = ({ formules, tooltipKey, prev, next }) => {
    if (!formules) return null;
    return (
      <div data-testid={`calculator-step-${step}`}>
        <p className="text-sm text-[#062133]/60 text-center mb-4">Type de nettoyage</p>
        <div className={`grid grid-cols-1 ${Object.keys(formules).length > 2 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3 max-w-2xl mx-auto`}>
          {Object.entries(formules).map(([k, v]) => (
            <div key={k} className="relative">
              <Opt s={ch.formule === k} hi={v.recommended} onClick={() => setCh(c => ({...c, formule: k}))} tid={`calc-formule-${k}`}>
                <div className="flex items-start justify-between gap-1">
                  <span className="block text-sm font-semibold text-[#062133]">{v.label}</span>
                  {TOOLTIPS[tooltipKey]?.[k] && (
                    <button onClick={(e) => { e.stopPropagation(); setTooltip(tooltip === k ? null : k); }}
                      className="shrink-0 w-5 h-5 rounded-full bg-[#336DBF]/10 flex items-center justify-center hover:bg-[#336DBF]/20" data-testid={`calc-info-${k}`}>
                      <Info size={12} className="text-[#336DBF]" />
                    </button>
                  )}
                </div>
                {v.recommended && <span className="block text-[10px] font-bold text-[#336DBF] mt-0.5 flex items-center gap-1"><Star size={10} /> Recommandé</span>}
              </Opt>
              {tooltip === k && TOOLTIPS[tooltipKey]?.[k] && <Tooltip text={TOOLTIPS[tooltipKey][k]} onClose={() => setTooltip(null)} />}
            </div>
          ))}
        </div>
        <Nav back={() => setStep(prev)} next={() => setStep(next)} dis={!ch.formule} />
      </div>
    );
  };
  const QtyStep = ({ prev, next, mx = 6, label = "Quantité" }) => (
    <div data-testid={`calculator-step-${step}`}>
      <p className="text-sm text-[#062133]/60 text-center mb-4">{label}</p>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 max-w-md mx-auto">
        {Array.from({length: mx}, (_, i) => i + 1).map(n => (
          <Opt key={n} s={ch.quantite === n} onClick={() => setCh(c => ({...c, quantite: n}))} tid={`calc-qty-${n}`}>
            <span className="block text-center text-lg font-bold text-[#062133]">{n}</span>
          </Opt>
        ))}
      </div>
      <Nav back={() => setStep(prev)} next={() => setStep(next)} dis={!ch.quantite} />
    </div>
  );

  // ===== CANAPE (10 steps: service,cat,prof,etat,supps,antitache,zone,pay,contact,result) =====
  const Canape = () => {
    const c = pr.canape; if (!c) return null;
    if (step === 2) return (<div data-testid="calculator-step-2"><p className="text-sm text-[#062133]/60 text-center mb-4">Type de canapé</p><div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto">{Object.entries(c.categories).map(([k, v]) => (<Opt key={k} s={ch.cat === k} onClick={() => setCh(x => ({...x, cat: k}))} tid={`calc-cat-${k}`}><span className="block text-sm font-semibold text-[#062133]">{v.label}</span></Opt>))}</div><Nav back={reset} next={() => setStep(3)} dis={!ch.cat} /></div>);
    if (step === 3) return (<div data-testid="calculator-step-3"><p className="text-sm text-[#062133]/60 text-center mb-4">Profondeur</p><div className="grid grid-cols-2 gap-3 max-w-md mx-auto">{[{k:"standard",l:"Standard"},{k:"grande_profondeur",l:"Grande profondeur"}].map(t => (<Opt key={t.k} s={ch.prof === t.k} onClick={() => setCh(x => ({...x, prof: t.k}))} tid={`calc-prof-${t.k}`}><span className="block text-sm font-semibold text-[#062133]">{t.l}</span></Opt>))}</div><Nav back={() => setStep(2)} next={() => setStep(4)} dis={!ch.prof} /></div>);
    if (step === 4) return (<div data-testid="calculator-step-4"><p className="text-sm text-[#062133]/60 text-center mb-4">État du canapé</p><div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-lg mx-auto">{Object.entries(c.etat).map(([k, v]) => (<Opt key={k} s={ch.etat === k} onClick={() => setCh(x => ({...x, etat: k}))} tid={`calc-etat-${k}`}><span className="block text-sm font-semibold text-[#062133]">{v.label}</span></Opt>))}</div><Nav back={() => setStep(3)} next={() => setStep(5)} dis={!ch.etat} /></div>);
    if (step === 5) { const supps = ch.supps || {}; return (<div data-testid="calculator-step-5"><p className="text-sm text-[#062133]/60 text-center mb-4">Suppléments (optionnel)</p><div className="grid grid-cols-2 gap-3 max-w-md mx-auto">{Object.entries(c.supplements).map(([k, v]) => (<button key={k} onClick={() => setCh(x => { const cur = {...(x.supps||{})}; cur[k] = !cur[k]; if(!cur[k]) delete cur[k]; return {...x, supps: cur}; })} data-testid={`calc-supp-${k}`} className={`p-3 border rounded-lg text-left flex items-center gap-2 w-full ${supps[k] ? "border-[#336DBF] bg-[#336DBF]/5 ring-1 ring-[#336DBF]" : "border-[#062133]/10 hover:border-[#336DBF]/50"}`}><div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${supps[k] ? "bg-[#336DBF] border-[#336DBF]" : "border-[#062133]/30"}`}>{supps[k] && <Check size={14} className="text-white" />}</div><span className="text-sm font-medium text-[#062133]">{v.label}</span></button>))}</div><Nav back={() => setStep(4)} next={() => setStep(6)} /></div>); }
    if (step === 6) return (<div data-testid="calculator-step-6"><p className="text-sm text-[#062133]/60 text-center mb-4">Traitement anti-tache</p><div className="grid grid-cols-2 gap-3 max-w-md mx-auto"><Opt s={ch.anti_tache === "non"} onClick={() => setCh(x => ({...x, anti_tache: "non"}))} tid="calc-at-non"><span className="block text-sm font-semibold text-[#062133]">Pas de traitement</span></Opt><Opt s={ch.anti_tache === "oui"} onClick={() => setCh(x => ({...x, anti_tache: "oui"}))} tid="calc-at-oui"><span className="block text-sm font-semibold text-[#062133]">Traitement anti-tache</span></Opt></div><Nav back={() => setStep(5)} next={() => setStep(7)} dis={!ch.anti_tache} /></div>);
    if (step === 7) return <ZoneStep prev={6} next={8} />;
    if (step === 8) return <PayStep prev={7} next={9} />;
    if (step === 9) return <ContactStep prev={8} />;
    if (step === 10) return <ResultStep />;
    return null;
  };

  // ===== TERRASSE (9 steps: service,sol,surface,formule,opts?,zone,pay,contact,result) =====
  const Terrasse = () => {
    const c = pr.terrasse;
    if (step === 2) return (<div data-testid="calculator-step-2"><p className="text-sm text-[#062133]/60 text-center mb-4">Type de sol</p><div className="grid grid-cols-2 gap-3 max-w-md mx-auto">{[{k:"beton",l:"Béton"},{k:"bois",l:"Bois"}].map(t => (<Opt key={t.k} s={ch.type_sol === t.k} onClick={() => setCh(x => ({...x, type_sol: t.k}))} tid={`calc-sol-${t.k}`}><span className="block text-sm font-semibold text-[#062133]">{t.l}</span></Opt>))}</div><Nav back={reset} next={() => setStep(3)} dis={!ch.type_sol} /></div>);
    if (step === 3) return <SurfaceStep prev={2} next={4} mx={200} def={20} />;
    if (step === 4) return <FormuleStep formules={c?.formules} tooltipKey="terrasse" prev={3} next={ch.type_sol === "bois" ? 5 : 6} />;
    if (step === 5 && ch.type_sol === "bois") {
      const opts = ch.opts || {};
      return (<div data-testid="calculator-step-5"><p className="text-sm text-[#062133]/60 text-center mb-4">Options extérieur bois</p><div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto">{c?.options_exterieur && Object.entries(c.options_exterieur).map(([k, v]) => (<button key={k} onClick={() => setCh(x => { const cur = {...(x.opts||{})}; cur[k] = !cur[k]; if(!cur[k]) delete cur[k]; return {...x, opts: cur}; })} data-testid={`calc-opt-${k}`} className={`p-3 border rounded-lg text-left flex items-center gap-2 w-full ${opts[k] ? "border-[#336DBF] bg-[#336DBF]/5 ring-1 ring-[#336DBF]" : "border-[#062133]/10 hover:border-[#336DBF]/50"}`}><div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${opts[k] ? "bg-[#336DBF] border-[#336DBF]" : "border-[#062133]/30"}`}>{opts[k] && <Check size={14} className="text-white" />}</div><span className="text-sm font-medium text-[#062133]">{v.label}</span></button>))}</div>{opts.stabilisation && <p className="text-sm text-[#336DBF] text-center mt-4">La stabilisation nécessite un devis personnalisé.</p>}<Nav back={() => setStep(4)} next={() => { if (opts.stabilisation) { if (onRequestDevis) onRequestDevis(sel?.name,"Terrasse bois stabilisation"); else document.getElementById("devis-section")?.scrollIntoView({behavior:"smooth"}); } else setStep(6); }} label={opts.stabilisation ? "Demander un devis" : "Suivant"} /></div>);
    }
    if (step === 6) return <ZoneStep prev={ch.type_sol === "bois" ? 5 : 4} next={7} />;
    if (step === 7) return <PayStep prev={6} next={8} />;
    if (step === 8) return <ContactStep prev={7} />;
    if (step === 9) return <ResultStep />;
    return null;
  };

  // ===== VITRES/MOQUETTE/TAPIS (7 steps: service,surface,formule,zone,pay,contact,result) =====
  const SurfaceFormuleFlow = ({ cfg, tooltipKey, mx, def }) => {
    if (step === 2) return <SurfaceStep prev={1} next={3} mx={mx} def={def} />;
    if (step === 3) return <FormuleStep formules={cfg} tooltipKey={tooltipKey} prev={2} next={4} />;
    if (step === 4) return <ZoneStep prev={3} next={5} />;
    if (step === 5) return <PayStep prev={4} next={6} />;
    if (step === 6) return <ContactStep prev={5} />;
    if (step === 7) return <ResultStep />;
    return null;
  };

  // ===== CHAISES/FAUTEUILS/MATELAS (6 steps: service,qty/taille,zone,pay,contact,result) =====
  const GenQty = ({ mx = 6, label }) => {
    if (step === 2) return <QtyStep prev={1} next={3} mx={mx} label={label} />;
    if (step === 3) return <ZoneStep prev={2} next={4} />;
    if (step === 4) return <PayStep prev={3} next={5} />;
    if (step === 5) return <ContactStep prev={4} />;
    if (step === 6) return <ResultStep />;
    return null;
  };
  const Matelas = () => {
    if (step === 2) { const t = pr.matelas?.tarifs; if (!t) return null; return (<div data-testid="calculator-step-2"><p className="text-sm text-[#062133]/60 text-center mb-4">Taille du matelas</p><div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-w-lg mx-auto">{Object.entries(t).map(([k, v]) => (<Opt key={k} s={ch.taille === k} onClick={() => setCh(x => ({...x, taille: k}))} tid={`calc-taille-${k}`}><span className="block text-center text-sm font-semibold text-[#062133]">{v.label}</span></Opt>))}</div><Nav back={reset} next={() => setStep(3)} dis={!ch.taille} /></div>); }
    if (step === 3) return <ZoneStep prev={2} next={4} />;
    if (step === 4) return <PayStep prev={3} next={5} />;
    if (step === 5) return <ContactStep prev={4} />;
    if (step === 6) return <ResultStep />;
    return null;
  };

  // ===== Devis Only =====
  const DevisOnly = () => (
    <div className="text-center" data-testid="calculator-devis-only">
      <div className="w-14 h-14 mx-auto mb-4 bg-[#336DBF]/10 rounded-full flex items-center justify-center"><FileText className="w-7 h-7 text-[#336DBF]" /></div>
      <p className="text-lg font-semibold text-[#062133] mb-2">{sel?.name}</p>
      <p className="text-sm text-[#062133]/60 mb-6 max-w-md mx-auto">Ce service nécessite un devis personnalisé. Notre équipe vous répond sous 2 heures.</p>
      <button onClick={() => { if (onRequestDevis) onRequestDevis(sel?.name, `Demande de devis pour ${sel?.name}`); else document.getElementById("devis-section")?.scrollIntoView({behavior:"smooth"}); }}
        className="px-8 py-3.5 bg-[#336DBF] text-white font-semibold rounded-md hover:bg-[#2A5A9E]" data-testid="calc-devis-custom">Demander un devis gratuit</button>
      <button onClick={reset} className="block mx-auto mt-3 text-sm text-[#062133]/60 hover:text-[#062133]">Choisir un autre service</button>
    </div>
  );

  // ===== Step 1 =====
  const Step1 = () => {
    const calc = services.filter(s => CALC.includes(s.slug));
    const devis = services.filter(s => !CALC.includes(s.slug));
    return (
      <div data-testid="calculator-step-1">
        <p className="text-sm text-[#062133]/60 text-center mb-4">Quel service vous intéresse ?</p>
        {calc.length > 0 && (<div className="mb-4"><p className="text-xs font-bold uppercase tracking-wider text-[#336DBF] mb-2 flex items-center gap-1.5"><Calculator size={14} /> Estimation instantanée</p><div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{calc.map(s => (<button key={s.slug} onClick={() => pick(s)} className="p-3 border border-[#336DBF]/20 rounded-lg text-left hover:border-[#336DBF] hover:bg-[#336DBF]/5 transition-all" data-testid={`calc-service-${s.slug}`}><span className="text-sm font-medium text-[#062133]">{s.name.replace("Nettoyage ", "")}</span></button>))}</div></div>)}
        {devis.length > 0 && (<div><p className="text-xs font-bold uppercase tracking-wider text-[#062133]/60 mb-2 flex items-center gap-1.5"><FileText size={14} /> Sur devis</p><div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{devis.map(s => (<button key={s.slug} onClick={() => pick(s)} className="p-3 border border-[#062133]/10 rounded-lg text-left hover:border-[#336DBF]/50 transition-all" data-testid={`calc-service-${s.slug}`}><span className="text-sm font-medium text-[#062133]">{s.name.replace("Nettoyage ", "")}</span></button>))}</div></div>)}
      </div>
    );
  };

  const content = () => {
    if (step === 1) return <Step1 />;
    if (!CALC.includes(slug)) return <DevisOnly />;
    if (slug === "nettoyage-canape") return <Canape />;
    if (slug === "nettoyage-terrasse") return <Terrasse />;
    if (slug === "nettoyage-vitres") return <SurfaceFormuleFlow cfg={pr.vitres?.formules} tooltipKey="vitres" mx={200} def={20} />;
    if (slug === "nettoyage-moquette") return <SurfaceFormuleFlow cfg={pr.moquette?.formules} tooltipKey="moquette" mx={500} def={30} />;
    if (slug === "nettoyage-tapis") return <SurfaceFormuleFlow cfg={pr.tapis?.formules} tooltipKey="tapis" mx={100} def={10} />;
    if (slug === "nettoyage-chaises") return <GenQty mx={6} label="Nombre de chaises" />;
    if (slug === "nettoyage-fauteuils") return <GenQty mx={6} label="Nombre de fauteuils" />;
    if (slug === "nettoyage-matelas") return <Matelas />;
    return null;
  };

  const pct = Math.round((step / steps) * 100);
  return (
    <div className="bg-white border border-[#062133]/10 rounded-lg p-6 sm:p-8 shadow-sm" data-testid="price-calculator">
      <h3 className="text-xl sm:text-2xl font-semibold text-[#062133] text-center mb-2">Estimez votre nettoyage en 30 secondes</h3>
      <p className="text-sm text-[#062133]/60 text-center mb-6">Simple, rapide, sans engagement</p>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {Array.from({length: steps}, (_, i) => i + 1).map(s => (
            <div key={s} className="flex items-center" style={{flex: s < steps ? 1 : 'none'}}>
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold shrink-0 ${
                step > s ? "bg-[#336DBF] text-white" : step === s ? "bg-[#336DBF] text-white ring-4 ring-[#336DBF]/20" : "bg-[#F4F6F8] text-[#062133]/60"}`}>
                {step > s ? <Check size={14} /> : s}
              </div>
              {s < steps && <div className={`h-0.5 flex-1 mx-0.5 ${step > s ? "bg-[#336DBF]" : "bg-[#F4F6F8]"}`} />}
            </div>
          ))}
        </div>
        <div className="w-full bg-[#F4F6F8] rounded-full h-1.5"><div className="bg-[#336DBF] h-1.5 rounded-full transition-all duration-300" style={{width:`${pct}%`}} /></div>
      </div>
      {content()}
    </div>
  );
}
